/**
 * Camera state, as the coach needs to reason about it.
 *
 * Expo reports a permission as `{ status, granted, canAskAgain }`. Those three
 * booleans collapse into four situations that need genuinely different handling,
 * and flattening them to `granted: boolean` loses the one that matters most:
 * a patient who declined once can be asked again, and a patient who declined
 * twice cannot — on iOS the second refusal makes the prompt a no-op forever. A
 * coach that keeps "asking" in that state is a button that does nothing.
 */

export type MediaPermissionState =
  /** Never asked. The OS prompt will appear. */
  | "undetermined"
  /** Asked and refused, but the OS will still show the prompt again. */
  | "denied"
  /** Refused for good. Only Settings can change it; asking again does nothing. */
  | "blocked"
  | "granted";

export type MediaPermissions = {
  camera: MediaPermissionState;
};

/** The shape both `expo-image-picker` and `expo-audio` return. */
export type ExpoPermissionResponse = {
  granted: boolean;
  canAskAgain: boolean;
};

export function toPermissionState(
  response: ExpoPermissionResponse | null | undefined,
  hasBeenAsked: boolean,
): MediaPermissionState {
  if (!response) return "undetermined";
  if (response.granted) return "granted";
  if (!response.canAskAgain) return "blocked";
  // `canAskAgain` is true both before the first prompt and after a soft refusal,
  // so the caller's memory of having asked is what separates them. Without it a
  // refusal reads as "never asked" and the sheet offers a prompt she already
  // said no to.
  return hasBeenAsked ? "denied" : "undetermined";
}

/** Whether asking again would actually surface a prompt. */
export function canPrompt(state: MediaPermissionState): boolean {
  return state === "undetermined" || state === "denied";
}

/**
 * Whether the live camera may be used.
 *
 * The microphone is deliberately not required. Voice is how the coach would
 * prefer to be interrupted, but a patient with both hands busy can still tap,
 * and refusing to run without a microphone would make the consent conditional on
 * giving more than she wanted to.
 */
export function cameraIsUsable(permissions: MediaPermissions): boolean {
  return permissions.camera === "granted";
}

/**
 * Where a refusal sends her.
 *
 * `blocked` is the only state that needs Settings, and it is the only one where
 * the honest thing is to say so rather than offer a button that cannot work.
 */
export function needsSystemSettings(state: MediaPermissionState): boolean {
  return state === "blocked";
}
